import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import { news, newsCategories } from '@/mocks/news';

const PAGE_STEP = 6;

export default function News() {
  const [category, setCategory] = useState('全部');
  const [keyword, setKeyword] = useState('');
  const [visible, setVisible] = useState(PAGE_STEP);

  useEffect(() => {
    setVisible(PAGE_STEP);
  }, [category, keyword]);

  useEffect(() => {
    document.title = '韩国电影资讯 - 影评新闻 · 电影动态 · 行业分析 - 韩国理论片';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) {
      desc.setAttribute(
        'content',
        '韩国理论片资讯频道，汇集影评新闻、电影动态与行业分析，第一时间了解韩国剧情电影与理论电影的行业动向。',
      );
    }
  }, []);

  const filtered = useMemo(() => {
    return news.filter((n) => {
      if (category !== '全部' && n.category !== category) return false;
      if (keyword) {
        const hay = [n.title, n.excerpt, n.author, n.category].join(' ').toLowerCase();
        if (!hay.includes(keyword.toLowerCase())) return false;
      }
      return true;
    });
  }, [category, keyword]);

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const [lead, ...rest] = shown;

  return (
    <SiteLayout sidebar>
      <Breadcrumb items={[{ label: '资讯' }]} />

      <header className="mb-6">
        <p className="font-label text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
          Newsroom
        </p>
        <h1 className="mt-2 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950">
          资讯新闻
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          汇集影评新闻、电影动态与行业分析，记录韩国剧情电影与理论电影的最新动向与产业观察。
        </p>
      </header>

      {/* 工具栏 */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="flex flex-wrap gap-2">
          {newsCategories.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setCategory(c)}
              className={`rounded-full px-3.5 py-1.5 font-label text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                category === c
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 sm:ml-auto sm:w-72 rounded-md border border-background-200 bg-background-50 px-3 h-10 focus-within:border-primary-400">
          <i className="ri-search-line text-foreground-400" aria-hidden="true" />
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索资讯关键词"
            className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
            aria-label="搜索资讯"
          />
          {keyword && (
            <button
              type="button"
              onClick={() => setKeyword('')}
              className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
              aria-label="清除关键词"
            >
              <i className="ri-close-line" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="ri-newspaper-line"
          title="没有找到相关资讯"
          description={`没有与「${keyword || category}」匹配的资讯内容。可以尝试更换关键词，或切换到其他资讯分类查看推荐内容。`}
          actionLabel="重置筛选"
          onAction={() => {
            setKeyword('');
            setCategory('全部');
          }}
        />
      ) : (
        <>
          {/* 头条 */}
          {lead && (
            <Link
              to={`/news/${lead.id}`}
              className="group grid grid-cols-1 md:grid-cols-2 rounded-lg overflow-hidden border border-background-200/80 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
            >
              <div className="relative w-full h-56 md:h-full min-h-[240px] bg-background-200">
                <img
                  src={lead.cover}
                  alt={`${lead.title} 配图`}
                  title={lead.title}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-5 md:p-7 flex flex-col justify-center">
                <span className="inline-flex w-fit items-center gap-1 rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-semibold text-accent-900">
                  {lead.category}
                </span>
                <h2 className="mt-3 font-heading text-xl md:text-2xl font-bold text-foreground-950 group-hover:text-primary-600 transition-colors">
                  {lead.title}
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600 line-clamp-3">{lead.excerpt}</p>
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-foreground-500">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-user-3-line" aria-hidden="true" />
                    {lead.author}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-calendar-line" aria-hidden="true" />
                    {lead.date}
                  </span>
                </div>
              </div>
            </Link>
          )}

          {/* 列表 */}
          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
            {rest.map((n) => (
              <Link
                key={n.id}
                to={`/news/${n.id}`}
                className="group flex flex-col rounded-lg overflow-hidden border border-background-200/80 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <div className="relative w-full h-44 bg-background-200">
                  <img
                    src={n.cover}
                    alt={`${n.title} 配图`}
                    title={n.title}
                    className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-2 left-2 rounded-md bg-background-50/90 px-2 py-0.5 font-label text-[11px] font-semibold text-foreground-800">
                    {n.category}
                  </span>
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h3 className="font-heading text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {n.title}
                  </h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 flex-1">{n.excerpt}</p>
                  <div className="mt-3 flex items-center justify-between text-xs text-foreground-500">
                    <span>{n.author}</span>
                    <span>{n.date}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 flex flex-col items-center gap-3">
            {hasMore ? (
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_STEP)}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 font-label text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-down-line" aria-hidden="true" />
                加载更多资讯
              </button>
            ) : (
              <p className="text-xs text-foreground-400">已显示全部 {filtered.length} 条资讯</p>
            )}
            <p className="text-xs text-foreground-400">
              已显示 {shown.length} / {filtered.length} 条（前端演示数据）
            </p>
          </div>
        </>
      )}
    </SiteLayout>
  );
}